import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getClient } from '../services/clientsService';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  updateProfile,
  deleteUser,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithCustomToken,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, functions } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { getProperties, getPropertiesByBroker } from '../services/dataService';
import { getUserProfile, setUserProfile } from '../services/userProfileService';
import { getBrokerLinks, pruneStaleBrokerLinks } from '../services/brokerLinksService';
import { setBrokerDisplayName } from '../services/brokerDisplayNameService';
import { getSharedWithMe, refreshSharedWithMeList } from '../services/shareService';
import { syncCurrentUserEmail } from '../services/userEmailService';
import { deleteAllUserData } from '../services/deleteAccountService';
import { requestPasswordResetEmail } from '../services/passwordResetEmailService';
import { isBrokerPersona, isPersonaAccessGranted } from '../constants/personas';
import { normalizeAdminRole } from '../constants/adminRoles';
import { subscribeFinancedDealsByOwner, subscribeFinancedDealsByLender, getFinancedDealsByOwnerOnce, getFinancedDealsByLenderOnce } from '../services/financedDealsService';
import {
  subscribeFundInvestorsByInvestor,
  syncFundInvestorDisplayNameForInvestorUser,
} from '../services/fundInvestorsService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState({ active: false, impersonatorUid: null });
  const [adminRole, setAdminRole] = useState(null);
  const [adminRoleResolved, setAdminRoleResolved] = useState(false);
  const [ownProperties, setOwnProperties] = useState([]);
  const [brokerProperties, setBrokerProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [userProfile, setUserProfileState] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [linkedBrokers, setLinkedBrokers] = useState([]);
  /** Liens brokerLinks dont le clients/{clientId} lie bien cet utilisateur comme emprunteur (évite partage checklist sans dossier emprunteur). */
  const [shareEligibleBrokerLinks, setShareEligibleBrokerLinks] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [financedDealsAsOwner, setFinancedDealsAsOwner] = useState([]);
  const [financedDealsAsLender, setFinancedDealsAsLender] = useState([]);
  const [fundInvestorsAsInvestor, setFundInvestorsAsInvestor] = useState([]);

  const properties = useMemo(
    () => [...ownProperties, ...brokerProperties],
    [ownProperties, brokerProperties],
  );

  const linkedBrokersSignature = useMemo(
    () =>
      linkedBrokers
        .map((l) => `${l.brokerUid || ''}:${l.clientId || ''}`)
        .sort()
        .join('|'),
    [linkedBrokers],
  );

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error('Error setting auth persistence:', error);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);
      if (!user) {
        setImpersonation({ active: false, impersonatorUid: null });
        setAdminRole(null);
        setAdminRoleResolved(true);
        try { sessionStorage.removeItem('impersonation'); } catch { /* ignore */ }
        return;
      }
      setAdminRoleResolved(false);
      try {
        const res = await user.getIdTokenResult();
        const active = res.claims?.impersonating === true;
        const impersonatorUid = res.claims?.impersonatorUid || null;
        setImpersonation({ active, impersonatorUid });
        setAdminRole(normalizeAdminRole(res.claims?.rentalyzerRole));
        setAdminRoleResolved(true);
        try {
          if (active) {
            sessionStorage.setItem('impersonation', JSON.stringify({ impersonatorUid }));
          } else {
            sessionStorage.removeItem('impersonation');
          }
        } catch { /* ignore */ }
      } catch (e) {
        console.warn('[auth] getIdTokenResult (impersonation claims)', e);
        setImpersonation({ active: false, impersonatorUid: null });
        setAdminRole(null);
        setAdminRoleResolved(true);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setOwnProperties([]);
      setBrokerProperties([]);
      setPropertiesLoading(false);
      return undefined;
    }
    setPropertiesLoading(true);
    let done = 0;
    const maybeDone = () => {
      done += 1;
      if (done === 3) setPropertiesLoading(false);
    };
    const unsub1 = getProperties(currentUser.uid, (props) => {
      setOwnProperties(props);
      maybeDone();
    });
    const unsub2 = getPropertiesByBroker(currentUser.uid, (props) => {
      setBrokerProperties(props);
      maybeDone();
    });
    const unsub3 = getSharedWithMe(currentUser.uid, (items) => {
      setSharedWithMe(items);
      maybeDone();
    });
    return () => {
      const u1 = unsub1;
      const u2 = unsub2;
      const u3 = unsub3;
      queueMicrotask(() => {
        u1?.();
        u2?.();
        u3?.();
      });
    };
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.email) {
      syncCurrentUserEmail(currentUser.uid, currentUser.email).catch(() => {});
    }
  }, [currentUser?.uid, currentUser?.email]);

  useEffect(() => {
    if (!currentUser) {
      setUserProfileState(null);
      setProfileLoading(false);
      setLinkedBrokers([]);
      return;
    }
    setProfileLoading(true);
    const userRef = doc(firestore, 'users', currentUser.uid);
    const unsubProfile = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setUserProfileState(null);
        } else {
          setUserProfileState({ id: snap.id, ...snap.data() });
        }
        setProfileLoading(false);
      },
      () => {
        setUserProfileState(null);
        setProfileLoading(false);
      }
    );
    return () => unsubProfile();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLinkedBrokers([]);
      return undefined;
    }
    pruneStaleBrokerLinks(currentUser.uid).catch(() => {});
    const unsub = getBrokerLinks(currentUser.uid, setLinkedBrokers);
    return () => unsub?.();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid || !linkedBrokers.length) {
      setShareEligibleBrokerLinks([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const eligible = [];
      for (const link of linkedBrokers) {
        if (!link.clientId || !link.brokerUid) continue;
        try {
          const c = await getClient(link.clientId);
          if (cancelled) return;
          if (!c || c.uid !== link.brokerUid) continue;
          if (c.clientUserId !== currentUser.uid) continue;
          eligible.push(link);
        } catch {
          /* permission refusée ou réseau */
        }
      }
      if (!cancelled) setShareEligibleBrokerLinks(eligible);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, linkedBrokersSignature]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setFinancedDealsAsOwner([]);
      setFinancedDealsAsLender([]);
      setFundInvestorsAsInvestor([]);
      return undefined;
    }
    const unsub1 = subscribeFinancedDealsByOwner(currentUser.uid, setFinancedDealsAsOwner);
    const unsub2 = subscribeFinancedDealsByLender(currentUser.uid, setFinancedDealsAsLender);
    const unsub3 = subscribeFundInvestorsByInvestor(currentUser.uid, setFundInvestorsAsInvestor);
    return () => {
      unsub1?.();
      unsub2?.();
      unsub3?.();
    };
  }, [currentUser?.uid]);

  // Publish public display name (brokerDisplayNames) for courtiers and prêteurs privés so investisseurs voient le bon libellé (hub chat, placements).
  useEffect(() => {
    if (!currentUser?.uid || !userProfile) return;
    if (!isPersonaAccessGranted(userProfile)) return;
    const p = userProfile.persona;
    if (!isBrokerPersona(p) && p !== 'preteur_prive') return;
    const name = userProfile.displayName || currentUser.displayName || '';
    setBrokerDisplayName(currentUser.uid, name, userProfile.persona).catch(() => {});
  }, [currentUser?.uid, currentUser?.displayName, userProfile?.persona, userProfile?.displayName]);

  const refreshUserProfile = useCallback(async () => {
    if (!currentUser) return;
    const profile = await getUserProfile(currentUser.uid);
    setUserProfileState(profile);
  }, [currentUser?.uid]);

  const updateUserProfile = useCallback(
    async (data) => {
      if (!currentUser) return;
      await setUserProfile(currentUser.uid, data);
      if (data.displayName !== undefined || data.avatarUrl !== undefined) {
        await updateProfile(auth.currentUser, {
          displayName: data.displayName ?? currentUser.displayName ?? '',
          photoURL: data.avatarUrl ?? currentUser.photoURL ?? '',
        });
      }
      if (data.displayName !== undefined) {
        const dn = typeof data.displayName === 'string' ? data.displayName : '';
        syncFundInvestorDisplayNameForInvestorUser(currentUser.uid, dn).catch((e) => {
          console.warn('[auth] syncFundInvestorDisplayNameForInvestorUser', e);
        });
      }
      await refreshUserProfile();
    },
    [currentUser, refreshUserProfile],
  );

  const completeOnboarding = useCallback(
    async (persona) => {
      if (!currentUser) return;
      const navLang = navigator.language || navigator.userLanguage || 'en';
      const defaultLanguage = navLang.startsWith('fr') ? 'fr' : 'en';
      const profileData = {
        persona: persona || 'investisseur',
        onboardingCompleted: true,
        displayName: currentUser.displayName ?? '',
        avatarUrl: currentUser.photoURL ?? '',
        language: defaultLanguage,
      };
      await setUserProfile(currentUser.uid, profileData);
      syncFundInvestorDisplayNameForInvestorUser(
        currentUser.uid,
        profileData.displayName != null ? String(profileData.displayName) : '',
      ).catch((e) => {
        console.warn('[auth] syncFundInvestorDisplayNameForInvestorUser (onboarding)', e);
      });
      setUserProfileState((prev) => ({
        ...prev,
        id: currentUser.uid,
        ...profileData,
      }));
      await refreshUserProfile();
    },
    [currentUser, refreshUserProfile],
  );

  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  /** TEMP (tests seulement) : Ctrl+X déconnecte — à retirer avant mise en prod. */
  useEffect(() => {
    const isEditableTarget = (el) => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return Boolean(el.closest('[contenteditable="true"]'));
    };
    const onKeyDown = (e) => {
      if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
      if (e.key !== 'x' && e.key !== 'X') return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (auth.currentUser) {
        signOut(auth).catch((err) => console.warn('[dev] Ctrl+X signOut', err));
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const loginWithGoogle = () => signInWithPopup(auth, googleProvider);

  const resetPassword = useCallback(
    (email, locale = 'fr') => requestPasswordResetEmail(email, locale),
    []
  );

  /**
   * Admin : se connecter en tant qu'un autre utilisateur.
   * @param {{ targetUid?: string, targetEmail?: string }} target
   */
  const startImpersonation = useCallback(async (target) => {
    const call = httpsCallable(functions, 'startImpersonation');
    const { data } = await call(target);
    if (!data?.token) throw new Error('Custom token manquant.');
    await signInWithCustomToken(auth, data.token);
    return data;
  }, []);

  /** Termine l'impersonation et reconnecte le compte admin d'origine. */
  const endImpersonation = useCallback(async () => {
    const call = httpsCallable(functions, 'endImpersonation');
    const { data } = await call();
    if (!data?.token) throw new Error('Custom token manquant.');
    await signInWithCustomToken(auth, data.token);
    return data;
  }, []);

  /** Re-authenticate with Google (opens popup). Use before sensitive operations like deleteAccount. */
  const reauthenticateWithGoogle = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    await reauthenticateWithPopup(user, googleProvider);
  }, []);

  /** Re-authenticate with email/password. Use before sensitive operations like deleteAccount. */
  const reauthenticateWithEmailPassword = useCallback(async (password) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Not signed in or no email');
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  }, []);

  /**
   * Delete the current user's account and all their data (properties, profile, shares, etc.).
   * Call reauthenticateWithGoogle() or reauthenticateWithEmailPassword(password) first to avoid auth/requires-recent-login.
   */
  const deleteAccount = useCallback(async () => {
    if (!currentUser) return;
    const uid = currentUser.uid;
    await deleteAllUserData(uid);
    const user = auth.currentUser;
    if (user && user.uid === uid) {
      await deleteUser(user);
    }
  }, [currentUser?.uid]);

  const refreshSharedWithMe = useCallback(() => {
    if (!currentUser?.uid) return;
    refreshSharedWithMeList(currentUser.uid).then(setSharedWithMe).catch((e) => {
      console.warn('refreshSharedWithMe', e);
    });
  }, [currentUser?.uid]);

  const refreshAdminRole = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setAdminRole(null);
      setAdminRoleResolved(true);
      return null;
    }
    const res = await user.getIdTokenResult(true);
    const role = normalizeAdminRole(res.claims?.rentalyzerRole);
    setAdminRole(role);
    setAdminRoleResolved(true);
    return role;
  }, []);

  const refreshFinancedDeals = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const [asOwner, asLender] = await Promise.all([
        getFinancedDealsByOwnerOnce(currentUser.uid),
        getFinancedDealsByLenderOnce(currentUser.uid),
      ]);
      setFinancedDealsAsOwner(asOwner);
      setFinancedDealsAsLender(asLender);
    } catch (e) {
      console.warn('refreshFinancedDeals', e);
    }
  }, [currentUser?.uid]);

  const value = {
    currentUser,
    loading,
    properties,
    propertiesLoading,
    sharedWithMe,
    refreshSharedWithMe,
    financedDealsAsOwner,
    financedDealsAsLender,
    fundInvestorsAsInvestor,
    refreshFinancedDeals,
    userProfile,
    profileLoading,
    linkedBrokers,
    shareEligibleBrokerLinks,
    refreshUserProfile,
    updateUserProfile,
    completeOnboarding,
    signup,
    login,
    logout,
    loginWithGoogle,
    resetPassword,
    deleteAccount,
    reauthenticateWithGoogle,
    reauthenticateWithEmailPassword,
    impersonation,
    startImpersonation,
    endImpersonation,
    adminRole,
    adminRoleResolved,
    refreshAdminRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

